import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import orderBy from 'lodash/orderBy'
import without from 'lodash/without'
import moment from 'moment'
import * as React from 'react'
import { Col, Row } from 'react-styled-flexboxgrid'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { DATE_FORMAT } from '@utils/constants'

import { ListCardLoader } from '../ListCard'
import { TimeIntervalProp, TimelineProps } from './interface'
import { Content, Dot, Inline, Line, LineCol, Time, TimeCol, TimeNow, TimeNowLine } from './style'
import PortalTimeFormat from '@components/PortalTimeFormat'

class Timeline<T> extends React.Component<TimelineProps<T> & WithUserTimeProps> {
  _isMounted = false
  currentTimeInterval = 0

  state = {
    interval: 15 as number,
    timeFormat: 'HH:mm' as string,
    datetimeFormat: DATE_FORMAT.DT as string,
    timeSlotMinutes: 0 as number,
    TimeIntervals: [] as Array<TimeIntervalProp<T>>,
  }

  componentDidMount() {
    this._isMounted = true

    if (this._isMounted) {
      this.getTimeIntervals()
      this.setTimeSlotMinutes()
    }

    this.currentTimeInterval = setInterval(() => {
      this.setTimeSlotMinutes()
    }, 10000)
  }

  componentDidUpdate(prevProps: TimelineProps<T>) {
    const { dataSource, startTime, endTime } = this.props

    if (
      !isEqual(prevProps.dataSource, dataSource) ||
      !isEqual(prevProps.startTime, startTime) ||
      !isEqual(prevProps.endTime, endTime)
    ) {
      this.getTimeIntervals()
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    if (this.currentTimeInterval) {
      clearInterval(this.currentTimeInterval)
    }
  }

  setTimeSlotMinutes = () => {
    const { getUserDate } = this.props
    const { interval } = this.state
    const roundedMinutes = Math.floor(getUserDate().clone().minute() / interval) * interval

    this.setState({
      timeSlotMinutes: getUserDate().clone().diff(getUserDate().clone().minute(roundedMinutes).second(0), 'minutes'),
    })
  }

  getStartTime = (data: T) => {
    const { timeFormat } = this.state
    return `${moment(get(data, 'StartTime'), 'HH:mm:ss').format(timeFormat)}-${moment(
      get(data, 'EndTime'),
      'HH:mm:ss'
    ).format(`${timeFormat} a`)}`
  }

  getTimeIntervals = () => {
    const { timeFormat } = this.state
    const { startTime, endTime } = this.props
    let TimeIntervals: Array<TimeIntervalProp<T>> = []

    if (
      moment(startTime, timeFormat, true).isValid() &&
      moment(endTime, timeFormat, true).isValid() &&
      moment(startTime, timeFormat, true).isBefore(moment(endTime, timeFormat, true))
    ) {
      for (
        const m = moment(startTime, timeFormat);
        m.isSameOrBefore(moment(endTime, timeFormat));
        m.add(15, 'minutes')
      ) {
        const time = m.format(timeFormat)
        TimeIntervals = [
          ...TimeIntervals,
          {
            time,
            dashed: m.format('mm') !== '00',
            data: this.getTimelineData(time),
          },
        ]
      }
    }

    this.setState({
      TimeIntervals,
    })
  }

  getTimelineData = (time: string) => {
    const { timeFormat, datetimeFormat } = this.state
    const { dataSource, dataTimeKey, dataTimeKeyFormat, toUserDate, currentDate } = this.props

    return orderBy(dataSource, (data: T) =>
      toUserDate(get(data, dataTimeKey), get(data, dataTimeKeyFormat), datetimeFormat)
    )
      .map((data: T, dataIndex: number) => ({
        ...data,
        dataIndex,
      }))
      .filter((data: T) => {
        const startTime = moment.utc(data?.[dataTimeKey]).tz(this.props.profile?.Timezone)
        const isStartSameAsCurrent = startTime.isSame(currentDate, 'day')

        const userTime = isStartSameAsCurrent
          ? moment(toUserDate(get(data, dataTimeKey), get(data, dataTimeKeyFormat), timeFormat), timeFormat)
          : moment().startOf('d')

        return (
          userTime.isBetween(moment(time, timeFormat), moment(time, timeFormat).add(15, 'minutes')) ||
          moment(time, timeFormat).isSame(userTime)
        )
      })
  }

  isInTimeRange = (time: moment.Moment | string) => {
    const { timeFormat } = this.state
    const { currentDate, getUserDate, getDateWithUserTimezone } = this.props
    const d = currentDate.format(DATE_FORMAT.BASIC)
    const st = moment(time, timeFormat).format('HH:mm:ss')
    const et = moment(time, timeFormat).format('HH:mm:ss')

    return (
      getUserDate().isSame(currentDate, 'day') &&
      getUserDate().isBetween(
        getDateWithUserTimezone(`${d} ${st}`),
        getDateWithUserTimezone(`${d} ${et}`).add(15, 'minutes')
      )
    )
  }

  flipCard = (selectedData: T[]) =>
    this.setState({
      TimeIntervals: this.state.TimeIntervals.map(({ data, ...rest }) => {
        if (isEqual(selectedData, data)) {
          return {
            ...rest,
            data: [...without(data, data[0]), data[0]],
          }
        }

        return {
          data,
          ...rest,
        }
      }),
    })

  render() {
    const { timeSlotMinutes, TimeIntervals, timeFormat } = this.state
    const { loading, renderTimelineContent, getUserDate } = this.props

    return (
      <Row>
        {TimeIntervals.map(({ time, dashed, data }, index) => (
          <React.Fragment key={`time-interval-${index}`}>
            <TimeCol xs={12} lg={1}>
              <Time bold={data.length > 0}>
                <PortalTimeFormat datetime={time} format={DATE_FORMAT.HM} />
              </Time>
            </TimeCol>
            <Col xs={1} lg={false} />
            <LineCol xs={11} lg={11}>
              <Inline>
                {!dashed && <Dot />}
                <Line dashed={dashed} />
              </Inline>
              <Content>
                {this.isInTimeRange(time) && (
                  <TimeNow minutes={timeSlotMinutes}>
                    <Time bold>{getUserDate().format(timeFormat)}</Time>
                    <TimeNowLine />
                    <TimeNowLine />
                  </TimeNow>
                )}

                {loading && <ListCardLoader />}

                {!loading && data.length > 0 && renderTimelineContent(data, time, this.flipCard, index)}
              </Content>
            </LineCol>
          </React.Fragment>
        ))}
      </Row>
    )
  }
}

export default WithUserTimeHOC(Timeline)
